import { Injectable, isDevMode } from "@angular/core";
import { KeycloakService } from "keycloak-angular";
import { BehaviorSubject } from "rxjs";
import { ConfigService } from "../config/config.service";
import { UtilitiesService } from "../utilities.service";
import { KEYCLOAK_CLIENT_ID, KEYCLOAK_DEFAULT_REALM } from "./keycloak.constants";

interface UserProfile {
  name: string;
  email: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthRealmService {
  // Tenant ID stored in a reactive BehaviorSubject
  private tenantId = new BehaviorSubject<string>("master");
  private userRoles: string[] | null = null;
  private userProfile: UserProfile | null = null;

  constructor(
    private configService: ConfigService,
    private keycloakService: KeycloakService,
    private utilityService: UtilitiesService
  ) {}

  async loginWithDifferentRealm(mappingKey?: string, clientId?: string) {
    const keycloakUrl = this.configService.getConfig("keycloakUrl");
    const rootDomain = (this.configService.getConfig("rootDomain") || "").trim();

    let origin = this.configService.getConfig("isDomainResolver")
      ? this.utilityService.getSubdomainUrl(mappingKey as string)
      : window.location.origin;

    // Defensive guard: strip the "master." prefix from the redirect origin if it
    // leaked in (e.g. alm-sync.master.dev.accello.virtusa.dev → alm-sync.dev.accello.virtusa.dev).
    // "master" is the Keycloak default realm, not a customer tenant subdomain.
    if (rootDomain) {
      const masterPrefix = `${KEYCLOAK_DEFAULT_REALM}.${rootDomain}`;  // master.dev.accello.virtusa.dev
      origin = origin.replace(masterPrefix, rootDomain);
    }

    const redirectPath = `${origin}${this.configService.buildAppPath("user/project")}`;
    const loginUrl = `${keycloakUrl}/realms/accello360-${mappingKey}-realm/protocol/openid-connect/auth?client_id=${
      clientId || KEYCLOAK_CLIENT_ID
    }&redirect_uri=${encodeURIComponent(redirectPath)}&response_type=code`;

    // Only persist realm selection in local development (not in production builds)
    if (isDevMode()) {
      sessionStorage.setItem("selectedRealm", mappingKey || KEYCLOAK_DEFAULT_REALM);
    }
    // Open Keycloak login with different realm in a new tab
    window.open(loginUrl, "_blank");
  }

  /**
   * Get the logged-in user's roles
   */
  getUserRoles(): string[] {
    if (this.userRoles === null) {
      const tokenParsed: any =
        this.keycloakService.getKeycloakInstance().tokenParsed;
      this.userRoles = tokenParsed?.realm_access?.roles || [];
    }
    return (this.userRoles as string[]);
  }

  /**
   * Get the logged-in user's profile information (name and email).
   * @returns The user's profile object.
   */
  getUserProfile(): UserProfile {
    if (this.userProfile === null) {
      const tokenParsed: any =
        this.keycloakService.getKeycloakInstance().tokenParsed;
      this.userProfile = {
        name: tokenParsed?.name || "",
        email: tokenParsed?.email || "",
      };
    }
    return this.userProfile;
  }


  /**
   * Check if the user has a specific role
   */
  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }

  /**
   * Check if the user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }
}
